import { AxiosInstance } from "axios";

interface IDownloadFile {
  method: "get" | "post";
  url: string;
  params?: unknown;
  data?: Record<string, string>;
  fileType: string;
  fileName: string;
  axiosClient: AxiosInstance;
}

export const EXCEL_FILE_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const PDF_FILE_TYPE = "application/pdf";
export const DOCX_FILE_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const downloadFile = async ({ axiosClient, method, url, params, data, fileName, fileType }: IDownloadFile) => {
  let response: unknown;
  let finalFileName = fileName;

  try {
    // Tạo axios instance mới không có response interceptor để tránh transform blob
    const axios = require("axios");
    const downloadClient = axios.create({
      baseURL: axiosClient.defaults.baseURL,
      timeout: axiosClient.defaults.timeout,
      headers: { ...axiosClient.defaults.headers },
    });

    // Chỉ thêm request interceptor để có auth token
    downloadClient.interceptors.request.use(
      async (config: any) => {
        // Copy logic từ vosClient request interceptor
        const { store } = require("../store/store");
        const { getDeviceId } = require("../pages/document/util");

        const token = store.getState().user?.current?.accessToken;
        const deviceId = await getDeviceId();

        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        if (config.headers) {
          config.headers.X_DEVICE_UDID = deviceId || "00000000-0000-0000-0000-000000000000";
        }

        return config;
      },
      (error: any) => Promise.reject(error),
    );

    const axiosResponse = await downloadClient({
      method,
      url,
      data,
      params,
      responseType: "blob",
    });

    response = axiosResponse.data;

    // Lấy filename từ content-disposition header nếu có
    const contentDisposition = axiosResponse.headers["content-disposition"];
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (fileNameMatch && fileNameMatch[1]) {
        finalFileName = fileNameMatch[1].replace(/['"]/g, "");
      }
    }
  } catch (err: any) {
    const resData = err?.response?.data ?? err;
    // Khi interceptor reject bằng error.response.data (có thể là Blob), ta parse JSON để lấy message.
    if (resData instanceof Blob && resData.type === "application/json") {
      const text = await resData.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json?.Message || json?.message || UNKNOWN_ERROR);
      } catch {
        throw new Error(text || UNKNOWN_ERROR);
      }
    }

    // Nếu backend trả object JSON (không phải Blob)
    if (resData && typeof resData === "object") {
      const msg =
        // @ts-ignore
        resData.Message ||
        // @ts-ignore
        resData.message ||
        err?.message ||
        UNKNOWN_ERROR;
      throw new Error(msg);
    }

    throw err;
  }

  // API có thể trả JSON (error) dù request blob -> parse và throw để caller hiển thị.
  if (response instanceof Blob && response.type === "application/json") {
    const text = await response.text();
    try {
      const json = JSON.parse(text);
      throw new Error(json?.Message || json?.message || UNKNOWN_ERROR);
    } catch {
      throw new Error(text || UNKNOWN_ERROR);
    }
  }

  // Đảm bảo blob luôn có đúng MIME type để file có thể mở được
  let blob: Blob;
  if (response instanceof Blob) {
    // Kiểm tra nếu blob rỗng hoặc quá nhỏ
    if (response.size === 0) {
      throw new Error("File tải về rỗng");
    }

    // Thử sử dụng blob gốc trước nếu có đúng MIME type
    if (response.type === fileType) {
      blob = response;
    } else {
      // Đọc Blob thành ArrayBuffer để đảm bảo dữ liệu nguyên vẹn khi tạo Blob mới
      const arrayBuffer = await response.arrayBuffer();
      blob = new Blob([arrayBuffer], {
        type: fileType,
      });
    }
  } else {
    blob = new Blob([response as unknown as BlobPart], {
      type: fileType,
    });
  }

  // Thử phương pháp download khác nếu blob nhỏ hơn expected
  if (blob.size < 1000) {
    // Thử đọc nội dung để kiểm tra
    const text = await blob.text();
    if (text.includes("error") || text.includes("Error")) {
      throw new Error("Server trả về lỗi thay vì file");
    }
  }

  const objectURL = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.style.display = "none";
  a.href = objectURL;
  a.download = finalFileName;

  document.body.appendChild(a);

  // Thử cả hai phương pháp click
  try {
    a.click();
  } catch (e) {
    a.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );
  }

  // Cleanup sau một chút để đảm bảo download hoàn tất
  setTimeout(() => {
    if (document.body.contains(a)) {
      document.body.removeChild(a);
    }
    window.URL.revokeObjectURL(objectURL);
  }, 1000);
};

interface Options {
  defaultMessage?: string;
}

const UNKNOWN_ERROR = "Có lỗi xảy ra";

const getError = async (error: any, options?: Options) => {
  if (typeof error === "string") return error;

  const responseData = error?.response?.data as unknown;
  if (responseData && typeof responseData === "object" && responseData !== null && "text" in responseData) {
    try {
      const text = await (responseData as { text(): Promise<string> }).text();
      const jsonData = JSON.parse(text);

      return jsonData.Message || jsonData.message || error?.message || options?.defaultMessage || UNKNOWN_ERROR;
    } catch {
      return (
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.message ||
        options?.defaultMessage ||
        UNKNOWN_ERROR
      );
    }
  }

  try {
    const json = JSON.parse(
      error?.response?.data?.Message || error?.response?.data?.message || error?.message || error?.Message,
    );

    return (
      json?.Message ||
      error?.response?.data?.Message ||
      error?.response?.data?.message ||
      error?.message ||
      error?.Message ||
      options?.defaultMessage ||
      UNKNOWN_ERROR
    );
  } catch {
    return (
      error?.response?.data?.Message ||
      error?.response?.data?.message ||
      error?.message ||
      error?.Message ||
      options?.defaultMessage ||
      UNKNOWN_ERROR
    );
  }
};

export default getError;
